import React from "react";
import classNames from "../helpers/classNames";
import { useI18n } from "../contexts/I18nContext";
import { Lock, LockOpen } from "lucide-react";

export default function ColorSwatch({
  color,
  locked,
  onToggleLock,
  ohuhuMode,
  showLockIcons = true,
  compact = false,
  mobileCompact = false,
  onSelect,
  replaceMode = false
}) {
  const { t } = useI18n();

  if (!color) {
    console.warn('ColorSwatch: color prop is undefined');
    return <div className="card-elegant p-4 bg-gray-100 text-gray-500">{t('colorSwatch.missing')}</div>;
  }

  const brand = typeof color.brand === 'string' ? color.brand : t('colorSwatch.unknownBrand');
  const name = typeof color.name === 'string' ? color.name : t('colorSwatch.untitled');
  const hasHex = typeof color.hex === 'string' && color.hex.trim() !== '';
  const safeHex = hasHex ? color.hex : '#E5E7EB';
  const displayHex = hasHex ? color.hex.toUpperCase() : '—';

  const isOhuhu = brand === "Ohuhu" || brand === "Ohuhu (Old)" || brand === "Ohuhu (New)";
  const isSanjoki = brand === "Sanjoki";
  const usesOldNew = isOhuhu || isSanjoki;
  const showOld = !usesOldNew ? false : (ohuhuMode === "old" || ohuhuMode === "both");
  const showNew = !usesOldNew ? false : (ohuhuMode === "new" || ohuhuMode === "both");

  const handleToggle = typeof onToggleLock === 'function' ? onToggleLock : () => {};

  const handleClick = replaceMode
    ? (typeof onSelect === 'function' ? () => onSelect(color) : () => {})
    : (typeof onSelect === 'function' ? () => onSelect(color) : handleToggle);

  let primaryCode = color?.code || '';
  if (usesOldNew) {
    if (ohuhuMode === "old") {
      primaryCode = color?.code_old || '';
    } else if (ohuhuMode === "new") {
      primaryCode = color?.code_new || '';
    } else if (ohuhuMode === "both") {
      const codes = [color?.code_old, color?.code_new].filter(Boolean);
      primaryCode = codes.length > 0 ? codes.join(" / ") : '';
    } else {
      primaryCode = color?.code_old || color?.code_new || '';
    }
  }
  if (!primaryCode) {
    primaryCode = displayHex !== '—' ? displayHex : '—';
  }

  // Calculate text color based on background darkness
  const getTextColor = (hex) => {
    // Remove # if present
    const cleanHex = hex.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(cleanHex.substr(0, 2), 16);
    const g = parseInt(cleanHex.substr(2, 2), 16);
    const b = parseInt(cleanHex.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return white for dark colors, dark for light colors
    return luminance > 0.5 ? '#1e293b' : '#ffffff';
  };
  const textColor = getTextColor(safeHex);

  const codeSegmentsRaw = primaryCode.split('/').map(segment => segment.trim()).filter(Boolean);
  const codeSegments = codeSegmentsRaw.length > 0 ? codeSegmentsRaw : [primaryCode.trim()];

  // Mobile compact mode: 4 columns, no lock, no name, just code and brand with smaller fonts
  if (mobileCompact) {
    return (
      <div
        className={classNames(
          "flex flex-col group cursor-pointer",
          replaceMode && "ring-4 ring-rose-pastel-400 rounded-xl shadow-[0_0_0_6px_rgba(244,114,182,0.35)] animate-pulse"
        )}
        onClick={handleClick}
      >
        <div
          className={classNames(
            "w-full aspect-square rounded-xl border-4 relative transition-all duration-200",
            locked ? "border-white" : "border-white shadow-inner"
          )}
          style={{
            backgroundColor: safeHex,
            ...(locked && { boxShadow: 'inset 0 0 0 3px rgba(255,255,255,0.7), inset 0 0 16px 4px rgba(255,255,255,0.35), 0 0 8px 2px rgba(255,255,255,0.2)' })
          }}
          title={safeHex}
        >
          <div className="absolute bottom-2 left-2 flex flex-wrap items-center gap-1">
            {codeSegments.map((segment, idx) => (
              <span
                key={idx}
                className="px-0.5 text-[9px] font-mono font-semibold uppercase tracking-[0.2em] leading-tight"
                style={{ color: textColor }}
              >
                {segment}
              </span>
            ))}
          </div>
        </div>
        <p className="text-[8px] font-semibold uppercase text-slate-500 truncate mt-1.5 px-1 tracking-[0.18em]">
          {brand}
        </p>
      </div>
    );
  }

  if (compact) {
    return (
      <div
        className={classNames(
          "flex flex-col group cursor-pointer",
          replaceMode && "animate-pulse"
        )}
        onClick={handleClick}
      >
        {/* Square color block — slightly smaller on desktop */}
        <div
          className={classNames(
            "w-full sm:w-[90%] sm:mx-auto aspect-square rounded-xl border-4 relative transition-colors duration-200",
            locked ? "border-white" : "border-white shadow-inner"
          )}
          style={{
            backgroundColor: safeHex,
            ...(locked && { boxShadow: 'inset 0 0 0 3px rgba(255,255,255,0.7), inset 0 0 16px 4px rgba(255,255,255,0.35), 0 0 8px 2px rgba(255,255,255,0.2)' })
          }}
          title={safeHex}
        >
          {showLockIcons && (
            <button
              className="absolute top-1.5 right-1.5 flex items-center justify-center transition-opacity duration-200"
              title={locked ? t('colorSwatch.unlock') : t('colorSwatch.lock')}
              aria-label={locked ? t('colorSwatch.unlock') : t('colorSwatch.lock')}
              style={{ color: textColor }}
            >
              {locked ? <Lock size={11} /> : <LockOpen size={11} />}
            </button>
          )}
          {/* Code overlaid bottom-left */}
          <div className="absolute bottom-2 left-2 flex flex-wrap items-center gap-1">
            {codeSegments.map((segment, idx) => (
              <span
                key={idx}
                className="px-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.3em]"
                style={{ color: textColor }}
              >
                {segment}
              </span>
            ))}
          </div>
        </div>
        {/* Brand below */}
        <p className="text-[8px] sm:text-[10px] font-semibold uppercase text-slate-500 truncate mt-1.5 px-2">
          {brand}
        </p>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "card-elegant overflow-hidden group flex items-center gap-3 sm:gap-4 px-1 sm:px-1 min-h-[54px]",
        replaceMode && "ring-4 ring-rose-pastel-400 shadow-[0_0_0_6px_rgba(244,114,182,0.35)] animate-pulse"
      )}
      onClick={handleClick}
    >
      <div
        className="flex-shrink-0 rounded-[12px] text-center w-[160px] sm:w-[160px] h-[44px] sm:h-[44] overflow-hidden shadow-inner flex items-center justify-center"
        style={{ backgroundColor: safeHex }}
        title={safeHex}
      >
        <div className="flex flex-wrap items-center justify-center gap-1 px-1 text-slate-900">
          {codeSegments.map((segment, idx) => (
            <span
              className="px-2 py-0.5 text-[11px] sm:text-xs font-mono font-semibold uppercase tracking-[0.3em]"
              style={{ color: textColor }}
              key={`${segment}-${idx}`}
            >
              {segment}
            </span>
          ))}
        </div>
      </div>

      <div className="flex-1 flex items-center min-h-[50px] gap-3 sm:gap-4">
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          <span className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.32em] text-slate-500 truncate">
            {brand}
          </span>
          <div className="text-sm sm:text-[15px] font-display font-semibold text-slate-800 leading-tight truncate">
            {name}
          </div>
        </div>

        {showLockIcons && (
          <button
            className={classNames(
              "w-8 h-8 sm:w-9 sm:h-9 rounded-full backdrop-blur transition-colors duration-200 shadow-soft flex items-center justify-center flex-shrink-0",
              locked
                ? "bg-green-300 hover:bg-white/80"
                : "bg-white/80 hover:bg-green-300"
            )}
            title={locked ? t('colorSwatch.unlock') : t('colorSwatch.lock')}
            aria-label={locked ? t('colorSwatch.unlock') : t('colorSwatch.lock')}
          >
            {locked ? <Lock size={16} className="text-slate-800" /> : <LockOpen size={16} className="text-slate-800" />}
          </button>
        )}
      </div>
    </div>
  );
}
